import { dayjs } from '@karve.it/core';
import { createSelector } from '@ngrx/store';
import { capitalize, cloneDeep } from 'lodash';
import MiniSearch from 'minisearch';
import { LoadingState } from 'src/app/utilities/state.util';

import {
    Asset,
    Attendee,
    CalendarEventForDispatchFragment,
    JobUser,
    Role
} from '../../../../generated/graphql.generated';

import { COLOR_MAP } from '../../../colors';
import { CAPABILITIES_BACKEND_MAP, CUSTOMER_ROLE_ATTRIBUTE, EventAttendeeRoles, eventTypeInfoMap } from '../../../global.constants';

import { isAutoGeneratedBlock } from './dispatch.default-blocks';
import {
    AdditionalEventAttendeeRoles,
    BOOK_OFF_EVENT_TYPE,
    CREW_LEAD_ROLE,
    CREW_MEMBER_ROLE,
    CrewSortOptions,
    DispatchCallState,
    DispatchEvent,
    DispatchFeature,
    DispatchGroupOptions,
    DispatchUserStatusOptions,
    DRIVER_ROLE,
    NON_BILLABLE_EVENT_TYPE,
    OPERATIONS_MANAGER_ROLE,
    OPS_MANAGER_EVENT_TYPE,
    OPS_TASK_EVENT_TYPE,
    PotentialCrewMember,
    RESERVE_EVENT_TYPE
} from './dispatch.reducer';

const ASSET_TYPES = ['Truck', 'Delivery'];

interface EventTimings {
    id: string;
    start: number;
    end: number;
}

interface CrewMetadata {
    events: Map<string, { start: number; end: number }>;
    eventsCount: number;
    conflictingEventIds: string[];
    hasConflict: boolean;
    revenue: number;
}

interface AttendeeTemplateVariablesData {
    events: CalendarEventForDispatchFragment[];
    eventsCount: number;
    hasConflict: boolean;
    user: any;
    misc: {
        estWorkingTime: string;
        itinerary: string;
    }
}

const DEFAULT_CREW_METADATA: CrewMetadata = {
    events: new Map(),
    eventsCount: 0,
    conflictingEventIds: [],
    hasConflict: false,
    revenue: 0,
};

/** Maps a 0–1 performance score to a human-readable label (snaps to nearest 0.2) */
function getRatingLabel(score: number): string {
    const labels: Record<string, string> = {
        '0.0': 'Any',
        '0.2': 'Basic',
        '0.4': 'Standard',
        '0.6': 'Skilled',
        '0.8': 'Expert',
        '1.0': 'Top',
    };
    const snapped = Math.round(Math.min(1, Math.max(0, score)) / 0.2) * 0.2;
    return labels[snapped.toFixed(1)] ?? score.toFixed(1);
}

/**
 * Compute the effective hourly rate ($/hr) incorporating the performance penalty.
 *
 * Formula: effectiveRate = wage + penalty * (1 - score) * (req / 0.4)
 *
 * - score: worker quality (0=Any → full penalty, 1=Top → no penalty ever)
 * - req:   event performance requirement — scales the penalty:
 *     req=0.0 → ×0   no performance cost at all
 *     req=0.4 → ×1   normal penalty (default)
 *     req=0.8 → ×2   double penalty
 *     req=1.0 → ×2.5 (linear progression)
 *
 * Examples at wage=$20/hr, penalty=$5, score=0.4 (Standard):
 *   req=0.0 → $20 + 5*0.6*0   = $20/hr  (req=0: no perf cost)
 *   req=0.4 → $20 + 5*0.6*1   = $23/hr  (req=0.4: default)
 *   req=0.8 → $20 + 5*0.6*2   = $26/hr  (req=0.8: double)
 * score=1.0 (Top), any req → $20 + 0    = $20/hr  (Top: never penalized)
 */
export function computeEffectiveRate(
    rateHourly: number | null | undefined,
    performanceScore: number | null | undefined,
    performanceRequirement = 0.4,
    penaltyPerHr = 5,
): number {
    const rate = (rateHourly || 0) / 100;
    const score = Math.min(1, Math.max(0, performanceScore ?? 0.4));
    const req = Math.min(1, Math.max(0, performanceRequirement));
    const reqMultiplier = req / 0.4;
    return rate + penaltyPerHr * (1 - score) * reqMultiplier;
}

/**
 * Overtime tier definition. Each tier defines a daily hour threshold and
 * the multiplier to apply to hours above that threshold.
 * E.g. BC: [{ thresholdHours: 8, multiplier: 1.5 }, { thresholdHours: 12, multiplier: 2.0 }]
 */
export interface OvertimeTier {
    thresholdHours: number;
    multiplier: number;
}

/**
 * Compute labor cost with multi-tier overtime support.
 *
 * If no tiers are provided, falls back to a single `otMultiplier` for all OT hours.
 * If tiers are provided, daily OT hours are split across tiers: hours above
 * each threshold get that tier's multiplier. Tiers should be sorted ascending
 * by thresholdHours. Hours up to the first threshold are at the base rate,
 * hours between the first and second threshold get the first multiplier, etc.
 *
 * Weekly OT always uses the single `otMultiplier` (tiered OT is daily-only
 * in most jurisdictions; weekly OT is a simpler fallback).
 */
export function computeOvertimeCost(
    regHours: number,
    otHours: number,
    wage: number,
    otMultiplier: number,
    dailyHoursBeforeThisEvent: number,
    dailyOtHours: number,
    tiers?: OvertimeTier[],
): number {
    if (!tiers || tiers.length === 0) {
        return (regHours * wage) + (otHours * wage * otMultiplier);
    }

    // Daily OT hours are split across tiers based on where they fall in the daily timeline.
    // OT starts after the reg hours of this event, so daily OT starts at
    // dailyHoursBeforeThisEvent + regHours.
    const regCost = regHours * wage;
    let otCost = 0;

    // Weekly OT hours use the simple multiplier (not tiered)
    const weeklyOtHours = otHours - dailyOtHours;
    otCost += weeklyOtHours * wage * otMultiplier;

    const sortedTiers = [...tiers].sort((a, b) => a.thresholdHours - b.thresholdHours);
    const dailyOtStart = dailyHoursBeforeThisEvent + regHours;

    let remainingOtHours = dailyOtHours;
    let currentHour = dailyOtStart;

    for (let i = 0; i < sortedTiers.length; i++) {
        if (remainingOtHours <= 0) break;

        const tier = sortedTiers[i];
        const nextThreshold = i + 1 < sortedTiers.length
            ? sortedTiers[i + 1].thresholdHours
            : Infinity;

        // Hours between this tier's threshold and the next tier's threshold
        const tierEnd = Math.min(currentHour + remainingOtHours, nextThreshold);
        const hoursInThisTier = Math.max(0, tierEnd - Math.max(currentHour, tier.thresholdHours));

        if (hoursInThisTier > 0) {
            otCost += hoursInThisTier * wage * tier.multiplier;
            remainingOtHours -= hoursInThisTier;
            currentHour += hoursInThisTier;
        }
    }

    // Any remaining OT hours not covered by tiers use the last tier's multiplier
    if (remainingOtHours > 0 && sortedTiers.length > 0) {
        otCost += remainingOtHours * wage * sortedTiers[sortedTiers.length - 1].multiplier;
    } else if (remainingOtHours > 0) {
        otCost += remainingOtHours * wage * otMultiplier;
    }

    return regCost + otCost;
}

// Helper function to create AttendeeWithName objects
const createAttendeeWithName = (attendee: Attendee | JobUser) => {
    if (!attendee) {
        return {
            user: null,
            role: null,
            locked: false,
            name: 'No customer set',
        };
    }
    const { user, role: attendeeRole } = attendee;
    const { givenName, familyName } = user;
    return {
        user: { ...user },
        role: attendeeRole,
        locked: (attendee as any).locked || false,
        name: [familyName, givenName].filter(Boolean).join(', '),
    };
};

// Helper function to format time
const formatTime = (timestamp: number) =>
    dayjs.tz(timestamp * 1000).format('hh:mm A');

// Function to check for overlapping events
const findConflictingEvents = (events: EventTimings[]): Set<string> => {
    const conflictingEvents = new Set<string>();

    if (events.length <= 1) return conflictingEvents;

    // Sort events by start time
    events.sort((a, b) => a.start - b.start);

    for (let i = 1; i < events.length; i++) {
        if (events[i].start < events[i - 1].end) {
            conflictingEvents.add(events[i - 1].id);
            conflictingEvents.add(events[i].id);
        }
    }

    return conflictingEvents;
};

const findConflicts = (map: Map<string, CrewMetadata>) => {
    for (const metadata of map.values()) {
        const eventsArray = Array.from(metadata.events.entries()).map(
            ([id, { start, end }]) => ({ id, start, end })
        );
        const conflictingEventIds = Array.from(
            findConflictingEvents(eventsArray)
        );

        metadata.hasConflict = Boolean(conflictingEventIds.length);
        metadata.conflictingEventIds = conflictingEventIds;
    }
};

const updateMetadataMap = (
    map: Map<string, CrewMetadata>,
    id: string,
    event: CalendarEventForDispatchFragment
) => {
    if (!map.has(id)) {
        map.set(id, {
            events: new Map(),
            eventsCount: 0,
            conflictingEventIds: [],
            hasConflict: false,
            revenue: 0,
        });
    }

    const metadata = map.get(id)!;

    metadata.events.set(event.id, {
        start: event.start,
        end: event.end,
    });
    metadata.eventsCount += 1;
    metadata.revenue += event.discountedSubTotal;
};

const parseNumberOfCrewMembers = (productName: string) => {
    const regex = /(\d+)\s*(?:Man|Crew)\s*(?:Hourly|Hourly Based|Truck & Travel|Rate|Special|Fee|(?!\d)|[^\d]*|)$/i;
    const match = productName.match(regex);
    return match ? parseInt(match[1], 10) : 0;
};

const getEventStyles = (eventType: string) => {
    const backgroundColor = eventTypeInfoMap[eventType]?.backgroundColor;

    return backgroundColor ? {
        backgroundColor: COLOR_MAP[backgroundColor].lightColor,
        color: COLOR_MAP[backgroundColor].textColor,
    } : {};
}

function generateTimelineWithEventDetails(orderedEvents: CalendarEventForDispatchFragment[]): any {

    const timeline = [];

    orderedEvents.forEach((event, index) => {

        const startDate = dayjs.tz(event.start * 1000);
        const endDate = dayjs.tz(event.end * 1000);
        const startTime = startDate.format('h:mm A');
        const endTime = endDate.format('h:mm A');
        const dateString = startDate.format('MMM D, YYYY');
        const durationHours = endDate.diff(startDate, 'hour');
        const hoursLabel = durationHours === 1 ? 'hour' : 'hours';
        // Requirement: `#5762` - exclude storage locations from the crew schedule preview.
        const locations = (event.locations || [])
            .filter((location) => location.type !== 'storage')
            .map((location) => `      • [${location.type}] ${location.location?.addressLineOne ?? ''}`)
            .join('\n');
        const truckDetails = event?.assets?.map((asset) => asset.name).join(', ') || 'N/A';

        const eventDetailsText = `
        ${index + 1}. ${capitalize(event.type)} Job
            • Event Name: ${event.title}
            • Date: ${dateString}
            • Time: ${startTime} – ${endTime}
            • Locations:
        ${locations}
            • Crew Size: ${(event.attendees || []).length}
            • Total Time: ${durationHours} ${hoursLabel}
            • Truck(s): ${truckDetails}
        `;

        timeline.push(eventDetailsText);
    });

    return timeline.join('\n');
}

export const {
    selectEvents,
    selectUsers,
    selectRoles,
    selectAssets,
    selectDispatchDate,
    selectDispatchDateTo,
    selectGroupBy,
    selectEventStatus,
    selectUserSearch,
    selectUserRole,
    selectUserStatus,
    selectUserSort,
    selectFilteredCrewIds,
    selectIsEditRateDialogOpen,
    selectIsCreatingUser,
    selectSelectedUserForRateEdit,
    selectSelectedCrewSettingsTab,
    selectSelectedUserBookOffEvents,
    selectCrewScheduleRecepients,
    selectWorkerHistory,
    selectCallState,
    selectHiddenMapRoutes,
    selectDockLocation
} = DispatchFeature;

// Helper to generate loading selector
// eslint-disable-next-line @ngrx/prefix-selectors-with-select
const createLoadingSelector = (callState: DispatchCallState) => createSelector(
    selectCallState,
    (state) =>
        state[callState] === LoadingState.PENDING ||
        state[callState] === LoadingState.LOADING ||
        state[callState] === LoadingState.MUTATING,
);

// Helper to generate error selector
// eslint-disable-next-line @ngrx/prefix-selectors-with-select
const createErrorSelector = (callState: DispatchCallState) => createSelector(
    selectCallState,
    (state) => {
        const cs = state[callState];
        if (typeof cs === 'object' && 'error' in cs) {
            return cs.error;
        }
        return null;
    },
);

export const selectEventsLoading = createLoadingSelector('events');
export const selectEventsLoaded = createSelector(
    selectCallState,
    (state) =>
        state['events'] === LoadingState.LOADED ||
        state['events'] === LoadingState.MUTATED,
);
export const selectEventsError = createErrorSelector('events');

export const selectUsersLoading = createLoadingSelector('users');
export const selectUsersLoaded = createSelector(
    selectCallState,
    (state) =>
        state['users'] === LoadingState.LOADED ||
        state['users'] === LoadingState.MUTATED,
);
export const selectUsersError = createErrorSelector('users');

export const selectRolesLoading = createLoadingSelector('roles');
export const selectRolesLoaded = createSelector(
    selectCallState,
    (state) =>
        state['roles'] === LoadingState.LOADED ||
        state['roles'] === LoadingState.MUTATED,
);
export const selectRolesError = createErrorSelector('roles');

export const selectAssetsLoading = createLoadingSelector('assets');
export const selectAssetsLoaded = createSelector(
    selectCallState,
    (state) =>
        state['assets'] === LoadingState.LOADED ||
        state['assets'] === LoadingState.MUTATED,
);
export const selectAssetsError = createErrorSelector('assets');

export const selectUpdateCrewLoading = createLoadingSelector('updateCrew');
export const selectUpdateCrewLoaded = createSelector(
    selectCallState,
    (state) =>
        state['updateCrew'] === LoadingState.LOADED ||
        state['updateCrew'] === LoadingState.MUTATED,
);
export const selectUpdateCrewError = createErrorSelector('updateCrew');

export const selectAssignCrewWithAILoading = createLoadingSelector('assignCrewWithAI');
export const selectAssignCrewInfeasibilityReasons = DispatchFeature.selectAssignCrewInfeasibilityReasons;
/** The assign-crew dialog stays open while running and while showing why a run failed. */
export const selectIsAssignCrewDialogOpen = createSelector(
    selectAssignCrewWithAILoading,
    selectAssignCrewInfeasibilityReasons,
    (loading, reasons) => loading || reasons !== null,
);
export const selectAssignCrewWithAILoaded = createSelector(
    selectCallState,
    (state) =>
        state['assignCrewWithAI'] === LoadingState.LOADED ||
        state['assignCrewWithAI'] === LoadingState.MUTATED,
);
export const selectAssignCrewWithAIError = createErrorSelector('assignCrewWithAI');

export const selectCrewScheduleSendLoading = createLoadingSelector('crewScheduleSend');
export const selectCrewScheduleSendLoaded = createSelector(
    selectCallState,
    (state) =>
        state['crewScheduleSend'] === LoadingState.LOADED ||
        state['crewScheduleSend'] === LoadingState.MUTATED,
);
export const selectCrewScheduleSendError = createErrorSelector('crewScheduleSend');


export const selectIsAnyLoading = createSelector(selectCallState, (callState) => {
    for (const key in callState) {
        if (callState[key] === LoadingState.LOADING) {
            return true;
        }
        if (callState[key] === LoadingState.MUTATING) {
            return true;
        }
    }

    return false;
});

export const selectCrewMetadata = createSelector(selectEvents, (events) => {
    const crewMetadataMap = new Map<string, CrewMetadata>();
    const assetMetadataMap = new Map<string, CrewMetadata>();

    for (const event of events) {
        for (const { user, role } of event.attendees) {
            if (role === EventAttendeeRoles.author) continue;
            updateMetadataMap(crewMetadataMap, user.id, event);
        }

        for (const { id } of event.assets) {
            updateMetadataMap(assetMetadataMap, id, event);
        }
    }

    findConflicts(crewMetadataMap);
    findConflicts(assetMetadataMap);

    return { crewMetadataMap, assetMetadataMap };
});

export const selectOvertimeTiers = createSelector(
    DispatchFeature.selectOvertimeTiers,
    (tiers) => tiers,
);

export const selectEventUserCosts = createSelector(
    selectEvents,
    selectUsers,
    selectWorkerHistory,
    selectDispatchDate,
    selectOvertimeTiers,
    (events, allUsers, workerHistory, dispatchDate, overtimeTiers) => {
        // Pre-calculate costs for all events/users
        const eventUserCostMap = new Map<string, Map<string, any>>();
        const sortedEvents = [...events].sort((a, b) => a.start - b.start);
        const userAccumulatedHours = new Map<string, { weekly: number, daily: number }>();

        const todayStartUnix = dayjs(dispatchDate).startOf('day').unix();

        const safeWorkerHistory = workerHistory || {};

        // Initialize weekly hours from history (excluding today)
        for (const userId of Object.keys(safeWorkerHistory)) {
            const historyEvents = safeWorkerHistory[userId];
            let weekly = 0;
            const user = allUsers.find(u => u.id === userId);
            const payPeriodStart = user?.payPeriodStart ?? 0;

            // Calculate pay period start date
            const currentDayOfWeek = dayjs(dispatchDate).day();
            const daysToSubtract = (currentDayOfWeek - payPeriodStart + 7) % 7;
            const payPeriodStartUnix = dayjs(dispatchDate).subtract(daysToSubtract, 'days').startOf('day').unix();

            for (const ev of historyEvents) {
                // Ignore events that start today or later (they are in `events` list or future)
                if (ev.start >= todayStartUnix) continue;
                if (ev.type === BOOK_OFF_EVENT_TYPE) continue;

                // Intersection with pay period
                const start = Math.max(ev.start, payPeriodStartUnix);
                const end = Math.min(ev.end, todayStartUnix); // Only count up to start of today

                if (end > start) {
                    weekly += (end - start) / 3600;
                }
            }
            userAccumulatedHours.set(userId, { weekly, daily: 0 });
        }

        // Process today's events
        for (const event of sortedEvents) {
            const durationHours = (event.end - event.start) / 3600;
            const eventId = event.id;
            if (!eventUserCostMap.has(eventId)) eventUserCostMap.set(eventId, new Map());

            const processedUsers = new Set<string>();

            for (const attendee of event.attendees) {
                if (attendee.role === EventAttendeeRoles.author) continue;
                const userId = attendee.user.id;

                if (processedUsers.has(userId)) continue;
                processedUsers.add(userId);

                let acc = userAccumulatedHours.get(userId);
                if (!acc) {
                    // If user not in history yet (e.g. no past events), init
                    // But we need to calc initial weekly too if they just didn't have history loaded?
                    // Assuming workerHistory contains all users relevant.
                    // If not, default to 0.
                    acc = { weekly: 0, daily: 0 };
                    userAccumulatedHours.set(userId, acc);
                }

                const user = allUsers.find(u => u.id === userId) || attendee.user;
                const wage = (user.rateHourly || 0);
                const otMultiplier = user.overtimeRate || 1.5;
                const maxDaily = user.maxHoursPerDay || 24;
                const maxWeekly = user.maxHoursPerWeek || 40;

                // Daily OT logic with multi-tier support.
                // If overtimeTiers are defined (e.g. BC: 1.5x after 8hr, 2x after 12hr),
                // hours above each threshold are paid at the corresponding multiplier.
                // Otherwise fall back to the single otMultiplier.
                const dailyRemaining = Math.max(0, maxDaily - acc.daily);
                const regDay = Math.min(durationHours, dailyRemaining);
                const otDay = Math.max(0, durationHours - regDay);

                // Weekly OT logic on the regDay portion
                const weeklyRemaining = Math.max(0, maxWeekly - acc.weekly);
                const regWeekly = Math.min(regDay, weeklyRemaining);
                const otWeekly = Math.max(0, regDay - regWeekly);

                const totalOT = otDay + otWeekly;
                const totalReg = regWeekly;

                // Requirement: Daily OT hours are split across configured tiers
                // (dispatch.overtimeTiers). Each tier covers [threshold, next threshold)
                // in the daily hour timeline. Weekly OT always uses the single
                // otMultiplier. When no tiers are configured, all OT uses otMultiplier.
                const cost = computeOvertimeCost(totalReg, totalOT, wage, otMultiplier, acc.daily, otDay, overtimeTiers);

                const isOvertime = totalOT > 0;

                eventUserCostMap.get(eventId).set(userId, {
                    cost,
                    isOvertime,
                    overtimeHours: totalOT,
                    hoursWorkedThisWeek: acc.weekly
                });

                // Update accumulators
                acc.daily += durationHours;
                // Only regDay counts towards weekly accumulation for 40h limit typically?
                // Or total hours?
                // If daily OT is paid, usually it doesn't count towards weekly OT threshold.
                // So we add `regDay` to weekly.
                acc.weekly += regDay;
            }
        }
        return eventUserCostMap;
    }
);

export const selectDerivedEvents = createSelector(
    selectEvents,
    selectCrewMetadata,
    selectUsers,
    selectEventUserCosts,
    (events, { crewMetadataMap, assetMetadataMap }, allUsers, eventUserCostMap) => {
        return events.map((event) => {
            const { attendees = [], assets = [], charges = [] } = event;
            const jobUsers = event.job?.users || [];
            let eventHasConflict = false;

            // Extract crew-related attendees
            const rawCrew = attendees
                .filter(
                    (attendee: Attendee) =>
                        attendee.role !== EventAttendeeRoles.author,
                )
                .map((attendee: Attendee) => {
                    const userId = attendee.user.id;
                    const {
                        eventsCount,
                        conflictingEventIds,
                        hasConflict,
                    } =
                        crewMetadataMap.get(userId) ||
                        DEFAULT_CREW_METADATA;

                    if (hasConflict) {
                        eventHasConflict = true;
                    }

                    const userDetails = allUsers.find((user) => user.id === userId);
                    const isDriver = userDetails?.capabilities?.includes('Driver');

                    // For synthetic events with minimal user data, merge in
                    // the full user details from allUsers so the name resolves.
                    const mergedAttendee = userDetails
                        ? { ...attendee, user: { ...attendee.user, ...userDetails } }
                        : attendee;

                    const costInfo = eventUserCostMap.get(event.id)?.get(userId);

                    const rating = userDetails?.performanceScore ?? 0.4;
                    const ratePerHour = (userDetails?.rateHourly || 0) / 100;
                    // Use the event's performanceRequirement as the multiplier
                    const perfReq = (event.metadata as any)?.performanceRequirement ?? 0.4;
                    const effectiveRate = computeEffectiveRate(userDetails?.rateHourly, rating, perfReq);
                    const reqLabel = `${perfReq.toFixed(1)} (${getRatingLabel(perfReq)})`;

                    const tooltip = costInfo ?
                        `Cost: $${(costInfo.cost / 100).toFixed(2)}\n` +
                        `Rate: $${ratePerHour.toFixed(2)}/hr\n` +
                        `Rating: ${rating.toFixed(1)} (${getRatingLabel(rating)})\n` +
                        `Event Req: ${reqLabel}\n` +
                        `Effective Rate: $${effectiveRate.toFixed(2)}/hr\n` +
                        `OT Hours: ${costInfo.overtimeHours.toFixed(1)}\n` +
                        `Weekly Hours: ${costInfo.hoursWorkedThisWeek.toFixed(1)}`
                        : (userDetails?.performanceScore != null || userDetails?.rateHourly)
                            ? `Rate: $${ratePerHour.toFixed(2)}/hr\n` +
                              `Rating: ${rating.toFixed(1)} (${getRatingLabel(rating)})\n` +
                              `Event Req: ${reqLabel}\n` +
                              `Effective Rate: $${effectiveRate.toFixed(2)}/hr`
                            : '';

                    return {
                        ...createAttendeeWithName(mergedAttendee),
                        eventsCount,
                        hasConflict,
                        conflictingEventIds,
                        isDriver,
                        totalCost: costInfo?.cost,
                        isOvertime: costInfo?.isOvertime,
                        overtimeHours: costInfo?.overtimeHours,
                        hoursWorkedThisWeek: costInfo?.hoursWorkedThisWeek,
                        tooltip
                    };
                });

            // Deduplicate crew and sort by role priority
            const uniqueCrewMap = new Map<string, any>();
            
            const getRolePriority = (role: string) => {
                switch (role) {
                    case EventAttendeeRoles.crewLead: return 1;
                    case EventAttendeeRoles.driver: return 2;
                    case EventAttendeeRoles.crewMember: return 3;
                    default: return 99;
                }
            };

            for (const member of rawCrew) {
                const existing = uniqueCrewMap.get(member.user.id);
                if (!existing) {
                    uniqueCrewMap.set(member.user.id, member);
                } else {
                    const existingPriority = getRolePriority(existing.role);
                    const newPriority = getRolePriority(member.role);
                    if (newPriority < existingPriority) {
                         // Update to higher priority role
                         uniqueCrewMap.set(member.user.id, member);
                    }
                }
            }
            
            const crew = Array.from(uniqueCrewMap.values());
            // Sort by priority
            crew.sort((a, b) => {
                const pA = getRolePriority(a.role);
                const pB = getRolePriority(b.role);
                if (pA !== pB) return pA - pB;
                return a.name.localeCompare(b.name);
            });

            // Extract truck-related assets
            const trucks = assets
                .filter(({ type }) =>
                    ASSET_TYPES.includes(type),
                )
                .map((asset: Asset) => {
                    const assetId = asset.id;
                    const {
                        eventsCount,
                        conflictingEventIds,
                        hasConflict,
                    } =
                        assetMetadataMap.get(assetId) ||
                        DEFAULT_CREW_METADATA;

                    if (hasConflict) {
                        eventHasConflict = true;
                    }

                    return {
                        ...asset,
                        eventsCount,
                        hasConflict,
                        conflictingEventIds,
                    };
                });

            const customer = jobUsers.find(
                (user) => user.role === CUSTOMER_ROLE_ATTRIBUTE,
            ) as JobUser;

            const laborCharge = charges.find(
                charge => charge?.product?.category === 'Labor',
            );

            const crewRequirement = parseNumberOfCrewMembers(laborCharge?.product?.name || '') || crew.length;

            const style = getEventStyles(event.type);

            const totalLaborCost = crew.reduce((total, crewMember) => {
                return total + (crewMember.totalCost || 0);
            }, 0);

            const totalRevenue = event.discountedSubTotal || 0;
            const laborPercentage = totalRevenue ? ((totalLaborCost / totalRevenue) * 100).toFixed(1) : 0;

            return {
                event,
                style,
                startTime: formatTime(event.start),
                endTime: formatTime(event.end),
                crew,
                customer: createAttendeeWithName(customer),
                trucks,
                crewRequirement,
                needsMoreCrew: crewRequirement > crew.length,
                laborCharge: {
                    name: laborCharge?.product?.name || '',
                    color: laborCharge?.product?.metadata?.color || '',
                },
                eventHasConflict,
                totalLaborCost,
                laborPercentage,
            } as DispatchEvent;
        });
    }
);

export const selectCrewDailyStats = createSelector(
    selectEventUserCosts,
    selectEvents,
    (eventUserCostMap, events) => {
        const stats = new Map<string, { totalHours: number, overtimeHours: number }>();

        // Exclude book-off events so PTO/leave hours do not inflate labour totals
        // or distort revenuePerLaborHour calculations.
        const workingEventIds = new Set(
            events
                .filter((e) => e.type !== BOOK_OFF_EVENT_TYPE)
                .map((e) => e.id),
        );
        const eventDurationMap = new Map<string, number>();
        events
            .filter((e) => workingEventIds.has(e.id))
            .forEach((e) => eventDurationMap.set(e.id, (e.end - e.start) / 3600));

        eventUserCostMap.forEach((userMap, eventId) => {
            if (!workingEventIds.has(eventId)) { return; }
            const duration = eventDurationMap.get(eventId) || 0;
            userMap.forEach((data, userId) => {
                const current = stats.get(userId) || { totalHours: 0, overtimeHours: 0 };
                current.totalHours += duration;
                current.overtimeHours += data.overtimeHours;
                stats.set(userId, current);
            });
        });

        return stats;
    }
);

/**
 * Ops manager events for the current dispatch date.
 * Ops managers are now first-class calendar events (type 'ops-manager') rather
 * than zone comments. This selector finds them among the derived events.
 */
export const selectOpsManagerEvents = createSelector(
    selectDerivedEvents,
    (events) => events.filter((e) => e.event.type === OPS_MANAGER_EVENT_TYPE)
);

/** The ops manager crew members assigned to the current dispatch date */
export const selectSelectedOpsManagers = createSelector(
    selectOpsManagerEvents,
    (opsEvents) => opsEvents.flatMap((e) => e.crew)
);

/** The user IDs of the ops managers assigned to the current dispatch date */
export const selectSelectedOpsManagerIds = createSelector(
    selectSelectedOpsManagers,
    (managers) => managers.map((m) => m.user.id)
);

/** The day note from the first ops-manager event's description (if any) */
export const selectOpsManagerDayNote = createSelector(
    selectOpsManagerEvents,
    (opsEvents) => opsEvents[0]?.event?.description || ''
);

export const selectTodaysRevenue = createSelector(
    selectDerivedEvents,
    selectCrewDailyStats,
    (events, crewDailyStats) => {
        const total = events.reduce((acc, event) => {
            return acc + (event.event.discountedSubTotal || 0);
        }, 0);

        const currency = events[0]?.event.job?.currency || 'USD';

        const totalLaborCost = events.reduce((acc, event) => { return acc + (event.totalLaborCost || 0); }, 0);
        const laborCostPercentage = total > 0 && totalLaborCost > 0 ? ((totalLaborCost / total) * 100).toFixed(1) : 0;

        // Sum unique crew hours across all events (avoid double-counting crew on multiple events)
        let totalLaborHours = 0;
        crewDailyStats.forEach((stats) => {
            totalLaborHours += stats.totalHours;
        });

        const revenuePerLaborHour = totalLaborHours > 0 ? total / totalLaborHours : 0;

        return { total, currency, totalLaborCost, laborCostPercentage, totalLaborHours, revenuePerLaborHour };
    }
);

export const selectConflictingEvents = createSelector(selectDerivedEvents, (events) => {
    // Only show conflicts for real job events — reserve/admin/ops-manager
    // blocks are placeholders, not scheduling conflicts
    return events.filter((event) =>
        event.eventHasConflict
        && event.event.type !== RESERVE_EVENT_TYPE
        && event.event.type !== NON_BILLABLE_EVENT_TYPE
        && event.event.type !== OPS_MANAGER_EVENT_TYPE
        && event.event.type !== OPS_TASK_EVENT_TYPE
    );
});

export const selectSortedEvents = createSelector(
    selectDerivedEvents,
    (events) => {
        const sortedEvents = cloneDeep(events);

        // Helper function for event sorting
        const compareEvents = (a: DispatchEvent, b: DispatchEvent) => {
            const startComparison = a.event.start - b.event.start; // ascending

            if (startComparison !== 0) {
                return startComparison;
            }

            // If start times are equal, sort by truck name ascending
            return a.event.createdAt - b.event.createdAt;
        };

        sortedEvents.sort(compareEvents);

        return sortedEvents;
    },
);

export const selectGroupedEvents = createSelector(
    selectSortedEvents,
    selectGroupBy,
    (events, groupBy) => {
        if (groupBy === DispatchGroupOptions.None) {
            return [{ group: 'All Events', events }];
        }

        const groupedEvents = new Map<string, DispatchEvent[]>();

        for (const event of events) {
            let key: string;
            switch (groupBy) {
                case DispatchGroupOptions.Route:
                    // Special event types group by their title (custom name)
                    // plus zone, so blocks from different zones are never merged.
                    // Each special event gets its own group (keyed by event ID)
                    // so multiple reserves/trainings with the same name stay separate.
                    if (event.event.type === OPS_MANAGER_EVENT_TYPE
                        || event.event.type === RESERVE_EVENT_TYPE
                        || event.event.type === NON_BILLABLE_EVENT_TYPE
                        || event.event.type === OPS_TASK_EVENT_TYPE) {
                        const title = event.event.title || event.event.type;
                        const zoneName = event.event.zones?.nodes?.[0]?.name;
                        const zoneSuffix = zoneName ? ` (${zoneName})` : '';
                        key = `${title}${zoneSuffix}__${event.event.id}`;
                    } else {
                        key = event.trucks.map(t => t.name).join(', ') || 'Unassigned';
                    }
                    break;
                case DispatchGroupOptions.StartTime:
                    key = event.startTime;
                    break;
                case DispatchGroupOptions.EventType:
                    key = event.event.type;
                    break;
                case DispatchGroupOptions.Status:
                    key = event.event.status;
                    break;
                case DispatchGroupOptions.RequiresAttention:
                    if (event.needsMoreCrew) {
                        key = 'Needs Crew';
                    } else if (event.trucks.length === 0) {
                        key = 'Needs an Asset';
                    } else {
                        key = 'OK';
                    }
                    break;
                case DispatchGroupOptions.PaidStatus:
                    key = 'Paid Status Not Implemented';
                    break;
                default:
                    key = 'Unknown';
            }

            if (!groupedEvents.has(key)) {
                groupedEvents.set(key, []);
            }
            groupedEvents.get(key).push(event);
        }

        const sortedGroups = Array.from(groupedEvents.entries()).map(([group, events]) => ({ group, events }));

        if (groupBy === DispatchGroupOptions.Route) {
            // Special blocks (ops manager / reserve / non-billable / ops task)
            // always sort ABOVE job routes; job routes sort by their earliest
            // event start (a 9:50 AM job with a shared truck must render above
            // a 12:30 PM one), falling back to the label alphabetically for
            // events without a start time.
            const isSpecialGroup = (g: { group: string; events: { event: { type: string } }[] }) =>
                g.events.some((e) =>
                    e.event.type === OPS_MANAGER_EVENT_TYPE
                    || e.event.type === RESERVE_EVENT_TYPE
                    || e.event.type === NON_BILLABLE_EVENT_TYPE
                    || e.event.type === OPS_TASK_EVENT_TYPE);
            const earliestStart = (g: { events: { event?: { start?: number } }[] }) =>
                Math.min(...g.events.map((e) => (e.event?.start || Number.MAX_SAFE_INTEGER)));
            sortedGroups.sort((a, b) => {
                const aSpecial = isSpecialGroup(a) ? 0 : 1;
                const bSpecial = isSpecialGroup(b) ? 0 : 1;
                if (aSpecial !== bSpecial) return aSpecial - bSpecial;
                const startDiff = earliestStart(a) - earliestStart(b);
                if (startDiff !== 0) return startDiff;
                return a.group.localeCompare(b.group);
            });
        }

        return sortedGroups;
    }
);

// TODO: Instead of making this separatel, we can simply add more properties to the existing crewMetadataMap
export const selectTemplateVariableData = createSelector(selectSortedEvents, (events) => {
    const crewMetadataMap = new Map<string, AttendeeTemplateVariablesData>();

    for (const event of events) {
        for (const eventAttendee of event.crew) {
            const { user, role } = eventAttendee;
            if (role === EventAttendeeRoles.author) continue;

            const { id } = user;
            if (!crewMetadataMap.has(id)) {
                crewMetadataMap.set(id, {
                    events: [],
                    eventsCount: 0,
                    hasConflict: false,
                    user: null,
                    misc: {
                        estWorkingTime: '',
                        itinerary: '',
                    }
                });
            }

            const metadata = crewMetadataMap.get(id)!;

            metadata.events.push(event.event);
            metadata.eventsCount += 1;
            metadata.hasConflict = event.eventHasConflict;
            metadata.user = {
                ...eventAttendee.user,
                name: eventAttendee.name
            };

            const eventsArr = metadata.events;
            if (eventsArr.length > 0) {
                const startTime = dayjs.tz(eventsArr[0].start * 1000);
                const endTime = dayjs.tz(eventsArr[eventsArr.length - 1].end * 1000);
                const durationHours = endTime.diff(startTime, 'hour');
                metadata.misc.estWorkingTime =
                    `${startTime.format('MMM D, YYYY h:mm A')} - ${endTime.format('MMM D, YYYY h:mm A')} ` +
                    `(${durationHours} hours)`;
                metadata.misc.itinerary = generateTimelineWithEventDetails(eventsArr);
            }
        }
    }

    // Iterate through the crewMetadataMap and set the estWorkingTime

    return crewMetadataMap;
});

export const selectDispatchTemplateVariableData = createSelector(
    selectTemplateVariableData,
    selectCrewScheduleRecepients,
    (map, recipients) => recipients.length === 1 ? map.get(recipients[0].user.id) : {},
);

export const selectCrewRoleIds = createSelector(
    selectRoles,
    // selectUserRole,
    (roles) => {
        const filterRole = (role: Role) => {
            return role.attributes.includes(CREW_LEAD_ROLE) ||
                role.attributes.includes(CREW_MEMBER_ROLE) ||
                role.attributes.includes(DRIVER_ROLE) ||
                role.attributes.includes(OPERATIONS_MANAGER_ROLE);
        };

        return (roles ?? []).filter(filterRole).map((role) => role.id);
    },
);

export const selectDerivedUsers = createSelector(selectUsers, (users) => {
    return users.map((user) => {
        const fullName = [user.familyName, user.givenName]
            .filter(Boolean)
            .join(', ');

        const isCrewLead = user.capabilities?.includes('CrewLead');
        const isDriver = user.capabilities?.includes('Driver');

        const role = isCrewLead
            ? EventAttendeeRoles.crewLead
            : EventAttendeeRoles.crewMember;

        return {
            name: fullName,
            user,
            role,
            isDriver,
        };
    });
});

export const selectEnrichedCrew = createSelector(
    selectDerivedUsers,
    selectCrewMetadata,
    selectDispatchDate,
    selectWorkerHistory,
    selectTemplateVariableData,
    selectCrewDailyStats,
    (users, { crewMetadataMap }, dispatchDate, workerHistory, templateData, dailyStats) => {
        if (!users.length) {
            return [];
        }

        const todayStartUnix = dayjs(dispatchDate).startOf('day').unix();
        const todayEndUnix = dayjs(dispatchDate).endOf('day').unix();
        const dayIndex = dayjs(dispatchDate).day(); // 0 (Sunday) to 6 (Saturday)

        // Map users to their metadata
        return users.map((user) => {
            const { eventsCount, revenue } = crewMetadataMap.get(user.user.id) || DEFAULT_CREW_METADATA;

            // Availability Check
            const isAvailableGlobally = user.user.available !== false; // Default to true if undefined
            let isAvailableToday = true;
            if (user.user.availability && user.user.availability.length > dayIndex) {
                const dayAvailability = user.user.availability[dayIndex];
                if (dayAvailability && dayAvailability.startsWith('N')) {
                    isAvailableToday = false;
                }
            }
            const isUnavailable = !isAvailableGlobally || !isAvailableToday;

            // Book-off Check
            let isBookedOff = false;
            const userEvents = workerHistory[user.user.id] || [];
            const bookOffs = userEvents.filter(e => e.type === BOOK_OFF_EVENT_TYPE);

            let totalBookOffDuration = 0;
            for (const bookOff of bookOffs) {
                // Calculate intersection with today
                const start = Math.max(bookOff.start, todayStartUnix);
                const end = Math.min(bookOff.end, todayEndUnix);
                if (end > start) {
                    totalBookOffDuration += (end - start);
                }
            }
            const totalBookOffHours = totalBookOffDuration / 3600;

            // Determine day duration for percentage calculation
            // Use maxHoursPerDay if reasonable (< 24), else default to 8 hours
            const maxHours = user.user.maxHoursPerDay || 24;
            const dayDurationHours = (maxHours < 24) ? maxHours : 8;
            const threshold = dayDurationHours * 0.4;

            if (totalBookOffHours > threshold) {
                isBookedOff = true;
            }

            const isAssigned = eventsCount > 0;
            const templateInfo = templateData.get(user.user.id);
            const stats = dailyStats.get(user.user.id);

            // Build rating info for the chip's info-circle tooltip
            // Use req=0.4 (neutral default) for the sidebar since there's no single event context
            const rating = user.user.performanceScore ?? 0.4;
            const ratePerHour = (user.user.rateHourly || 0) / 100;
            const effectiveRate = computeEffectiveRate(user.user.rateHourly, rating, 0.4);
            const ratingTooltipText =
                `Rate: $${ratePerHour.toFixed(2)}/hr\n` +
                `Rating: ${rating.toFixed(1)} (${getRatingLabel(rating)})\n` +
                `Effective Rate @ Standard: $${effectiveRate.toFixed(2)}/hr`;

            return {
                ...user,
                eventsCount,
                revenue,
                isUnavailable,
                isBookedOff,
                isAssigned,
                estWorkingTime: templateInfo?.misc?.estWorkingTime,
                // itinerary: templateInfo?.misc?.itinerary, // Removed as per feedback
                hoursWorked: stats?.totalHours || 0,
                overtimeHours: stats?.overtimeHours || 0,
                ratingTooltipText,
            };
        });
    }
);

export const selectCrewForLocalSearch = createSelector(
    selectEnrichedCrew,
    selectUserRole,
    selectUserStatus,
    selectFilteredCrewIds,
    selectUserSort,
    (crew, userRole, userStatus, filteredCrewIds, userSort): PotentialCrewMember[] => {
        let filteredUsers = crew;

        if (filteredCrewIds !== null) {
            const allowedIds = new Set(filteredCrewIds);
            filteredUsers = filteredUsers.filter((u) => allowedIds.has(u.user.id));
        }

        if (userRole && userRole !== AdditionalEventAttendeeRoles.All) {
            filteredUsers = filteredUsers.filter((u) => u.user.capabilities?.includes(CAPABILITIES_BACKEND_MAP[userRole] as string));
        }

        // Filter by Status
        if (userStatus && userStatus !== DispatchUserStatusOptions.All) {
            filteredUsers = filteredUsers.filter(member => {
                const isActive = (member.user.available === true
                    && member.user.rateHourly) || member.isAssigned;

                switch (userStatus) {
                    case DispatchUserStatusOptions.Active:
                        return isActive;
                    case DispatchUserStatusOptions.Inactive:
                        return !isActive;
                    case DispatchUserStatusOptions.Assigned:
                        return isActive && member.isAssigned;
                    case DispatchUserStatusOptions.Unassigned:
                        return isActive && !member.isAssigned;
                    case DispatchUserStatusOptions.Unavailable:
                        // Active users who are unavailable OR booked off
                        return isActive && (member.isUnavailable || member.isBookedOff);
                    case DispatchUserStatusOptions.Available:
                        // Active users who are NOT unavailable AND NOT booked off
                        return isActive && !member.isUnavailable && !member.isBookedOff;
                    default:
                        return true;
                }
            });
        }

        // Sort: Unavailable/BookedOff at the bottom
        return filteredUsers.sort((a, b) => {
            const aUnavailable = a.isUnavailable || a.isBookedOff;
            const bUnavailable = b.isUnavailable || b.isBookedOff;

            if (aUnavailable && !bUnavailable) return 1;
            if (!aUnavailable && bUnavailable) return -1;

            switch (userSort) {
                case CrewSortOptions.LastName:
                    return (a.user.familyName || '').localeCompare(b.user.familyName || '');
                case CrewSortOptions.FirstName:
                    return (a.user.givenName || '').localeCompare(b.user.givenName || '');
                case CrewSortOptions.RateHourly:
                    return (b.user.rateHourly || 0) - (a.user.rateHourly || 0);
                case CrewSortOptions.EffectiveRate:
                    // Sort ascending: lowest effective rate first (best value)
                    // Uses default event requirement of 0.4 (Standard) for the sidebar
                    return computeEffectiveRate(a.user.rateHourly, a.user.performanceScore, 0.4)
                         - computeEffectiveRate(b.user.rateHourly, b.user.performanceScore, 0.4);
                case CrewSortOptions.Availability:
                default:
                    return 0;
            }
        });
    },
);

export const selectPotentialCrew = createSelector(
    selectCrewForLocalSearch,
    selectUserSearch,
    (crew, userSearch) => {
        if (!userSearch) {
            return crew;
        }

        const miniSearch = new MiniSearch({
            fields: ['name', 'email', 'phone'],
            storeFields: ['id'],
            searchOptions: {
                prefix: true,
                fuzzy: true,
            },
        });
        miniSearch.addAll(crew.map((user) => ({ ...user, id: user.user.id })));
        const results = miniSearch.search(userSearch);
        const ids = new Set(results.map((r) => r.id));
        return crew.filter((u) => ids.has(u.user.id));
    }
);

export const selectWorkingCrew = createSelector(
    selectPotentialCrew,
    (potentialCrew) => potentialCrew.filter((crew) => crew.eventsCount > 0),
);

export const selectPotentialAssets = createSelector(
    selectAssets,
    selectCrewMetadata,
    (assets, { assetMetadataMap }) => {
        if (!assets.length) {
            return [];
        }

        // Map assets to their metadata
        return assets.map((asset) => {
            const { eventsCount, revenue } =
                assetMetadataMap.get(asset.id) || DEFAULT_CREW_METADATA;

            return {
                ...asset,
                eventsCount,
                revenue,
            };
        });
    },
);

export const selectVisibleMapRoutes = createSelector(
    selectGroupedEvents,
    selectHiddenMapRoutes,
    (groupedEvents, hiddenRoutes) => {
        return groupedEvents.filter(group => !hiddenRoutes.includes(group.group));
    }
);

// Requirement: `#5604` - Indicate when all dispatch events have sufficient crew. Will be used to conditionally execute changes to the UI.
export const selectAreEventRequirementsSatisfied = createSelector(
    selectDerivedEvents,
    (events) => events.length > 0 && events.every(event => !event.needsMoreCrew)
);

// ─── Send Schedule to Crew dialog selectors ─────────────────────────────────

export const selectIsSendScheduleDialogOpen = DispatchFeature.selectIsSendScheduleDialogOpen;

export const selectSendScheduleSelectedCrew = DispatchFeature.selectSendScheduleSelectedCrew;
export const selectSendScheduleAssetNotes = DispatchFeature.selectSendScheduleAssetNotes;
export const selectSendScheduleBatches = createSelector(
    DispatchFeature.selectDispatchState,
    (state) => state.sendScheduleBatches,
);

export const selectSendScheduleCrewNotes = DispatchFeature.selectSendScheduleCrewNotes;
export const selectSendSchedulePreviews = DispatchFeature.selectSendSchedulePreviews;
export const selectSendScheduleAssetThreads = DispatchFeature.selectSendScheduleAssetThreads;
export const selectSendScheduleCrewStatuses = DispatchFeature.selectSendScheduleCrewStatuses;

/**
 * Builds the route/crew tree for the Send Schedule dialog.
 * Grouped by asset/truck directly from events, independent of the current
 * groupBy UI setting — so Select All works even when dispatch page is grouped
 * by something other than Route.
 */
export const selectSendScheduleRouteTree = createSelector(
    selectDerivedEvents,
    selectEnrichedCrew,
    DispatchFeature.selectSendScheduleSelectedCrew,
    DispatchFeature.selectSendScheduleCrewStatuses,
    DispatchFeature.selectSendScheduleAssetThreads,
    (allEvents, enrichedCrew, selectedCrew, crewStatuses, assetThreads) => {
        // Group events by their primary truck asset
        const groupMap = new Map<string, { assetId: string; routeName: string; events: typeof allEvents }>();
        for (const event of allEvents) {
            const truck = event.trucks?.[0];
            if (!truck) { continue; }
            const key = truck.id;
            if (!groupMap.has(key)) {
                groupMap.set(key, { assetId: truck.id, routeName: truck.name, events: [] });
            }
            groupMap.get(key).events.push(event);
        }
        // Represent groups as pseudo-array for shared map() logic below
        const groupedEvents = Array.from(groupMap.values()).map((g) => ({ group: g.routeName, assetId: g.assetId, events: g.events }));

        return groupedEvents
            .map((group) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const primaryAssetId: string = (group as any).assetId || group.events[0]?.trucks[0]?.id || group.group;
                // Get unique crew members assigned to events in this route
                const crewOnRoute = new Map<string, typeof enrichedCrew[0]>();
                const assetIdsOnRoute = new Set<string>();

                for (const event of group.events) {
                    for (const truck of event.trucks) {
                        assetIdsOnRoute.add(truck.id);
                    }
                    for (const attendee of event.crew) {
                        if (attendee.user?.id) {
                            const crewMember = enrichedCrew.find((c) => c.user.id === attendee.user.id);
                            if (crewMember) {
                                crewOnRoute.set(attendee.user.id, crewMember);
                            }
                        }
                    }
                }

                const assetSelected = selectedCrew[primaryAssetId] || {};

                const crewList = Array.from(crewOnRoute.values()).map((crewMember) => {
                    const userId = crewMember.user.id;
                    const statusKey = `${primaryAssetId}:${userId}`;
                    const email = crewMember.user.email || '';
                    const phone = crewMember.user.phone || '';
                    const hasValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
                    const hasValidPhone = phone.replace(/\D/g, '').length >= 7;
                    return {
                        crewMember,
                        isSelected: assetSelected[userId] ?? false,
                        sendStatus: crewStatuses[statusKey] || 'not-sent' as const,
                        email,
                        phone,
                        hasValidEmail,
                        hasValidPhone,
                        canSend: hasValidEmail || hasValidPhone,
                    };
                });

                // Check if any events on this route need more crew
                const hasMissingCrew = group.events.some((e) => e.needsMoreCrew);

                return {
                    routeName: group.group,
                    assetId: primaryAssetId,
                    assetIds: Array.from(assetIdsOnRoute),
                    events: group.events,
                    crew: crewList,
                    hasMissingCrew,
                    threads: assetThreads[primaryAssetId] || [],
                };
            });
    }
);

/**
 * Count of crew members that are both selected AND can be contacted (canSend).
 * Derived from the live route tree so stale selections from previous dates or
 * removed routes cannot inflate the count and make the submit button misleading.
 */
export const selectSendScheduleSelectedCount = createSelector(
    selectSendScheduleRouteTree,
    (routeTree) => routeTree.reduce(
        (count, route) => count + route.crew.filter((item) => item.isSelected && item.canSend).length,
        0,
    ),
);

export const selectSendScheduleSubmitting = createLoadingSelector('sendScheduleSubmit');

// ── Pending changes ──────────────────────────────────────────────────────────
export const selectPendingChanges = DispatchFeature.selectPendingChanges;
export const selectHasPendingChanges = createSelector(
    selectPendingChanges,
    (changes) => changes.length > 0,
);
export const selectIsSavingChanges = DispatchFeature.selectIsSavingChanges;
export const selectSaveError = createSelector(
    DispatchFeature.selectSaveChangesError,
    (error) => error || null,
);
export const selectAutosaveEnabled = DispatchFeature.selectAutosaveEnabled;
export const selectLastEventsLoadedAt = DispatchFeature.selectLastEventsLoadedAt;
export const selectLastPendingChangeAt = DispatchFeature.selectLastPendingChangeAt;
export const selectPendingChangeCount = createSelector(
    selectPendingChanges,
    (changes) => changes.length,
);

// ── Default blocks (calendarEvents.defaultBlocks) ────────────────────────────
export const selectDefaultBlocksConfig = createSelector(
    DispatchFeature.selectDefaultBlocksConfig,
    DispatchFeature.selectDefaultBlocksConfigLoading,
    (entries, loading) => ({ entries, loading }),
);

/** ISO dates (YYYY-MM-DD) whose default blocks have already been ensured. */
export const selectDefaultBlocksEnsuredDates = DispatchFeature.selectDefaultBlocksEnsuredDates;

/**
 * Auto-generated blocks currently on the board for the dispatch date — the
 * reset button's delete targets. Empty while events are still loading.
 */
export const selectAutoGeneratedBlocks = createSelector(
    DispatchFeature.selectEvents,
    DispatchFeature.selectDispatchDate,
    (
        events,
        date,
    ) => {
        const dayStart = dayjs(date).startOf('day');
        const dayEnd = dayjs(date).add(1, 'day').startOf('day');
        return events.filter((event) => {
            if (event?.type !== 'ops-manager' && event?.type !== 'reserve' && event?.type !== 'non-billable') return false;
            if (!isAutoGeneratedBlock(event)) return false;
            const start = dayjs((event.start ?? 0) * 1000);
            // Containment within the dispatch day, OR an overnight block whose
            // start precedes midnight and whose end reaches into the day.
            if (!start.isBefore(dayStart) && start.isBefore(dayEnd)) return true;
            const end = dayjs((event.end ?? 0) * 1000);
            return start.isBefore(dayStart) && end.isAfter(dayStart);
        });
    },
);

// ─── Ops Manager pool (derived from crew with Operations capability) ─────────
export const selectOpsManagerPool = createSelector(
    selectEnrichedCrew,
    (crew) => crew
        .filter((c) => c.user.capabilities?.includes('Operations'))
        .map((c) => ({
            id: c.user.id,
            name: c.name,
            phone: c.user?.phone,
            rateHourly: c.user?.rateHourly,
        }))
);

// ─── Grouped events with placeholder routes ──────────────────────────────────
/**
 * Grouped events with placeholder route columns injected.
 * Only shows routes that have events — no empty placeholders.
 */
export const selectGroupedEventsWithPlaceholders = createSelector(
    selectGroupedEvents,
    selectGroupBy,
    (groups, groupBy) => {
        if (groupBy !== DispatchGroupOptions.Route) {
            return groups;
        }

        // Preserve the earliest-start ordering from selectGroupedEvents
        // (job routes by earliest event start, alphabetical fallback) while
        // sorting the special blocks (ops manager / reserve / admin /
        // non-billable / ops task) above them in their fixed order.
        // Sorting trucks alphabetically here discarded the start-time order.
        const specialOrder = ['Ops Manager', 'Reserve', 'Admin', 'Non-Billable', 'OPS'];
        const result = [...groups];
        result.sort((a, b) => {
            const aLabel = a.group.split('__')[0];
            const bLabel = b.group.split('__')[0];
            const aSpecial = specialOrder.findIndex(s => aLabel.startsWith(s));
            const bSpecial = specialOrder.findIndex(s => bLabel.startsWith(s));
            if (aSpecial !== -1 && bSpecial !== -1) return aSpecial - bSpecial;
            if (aSpecial !== -1) return -1;
            if (bSpecial !== -1) return 1;
            return 0;
        });

        return result;
    }
);

export interface RouteGroupWithMetrics {
    group: string;
    groupLabel: string;
    events: DispatchEvent[];
    crew: {
        name: string; isCrewLead: boolean; isDriver: boolean; userId: string;
        hasConflict?: boolean; conflictingEventIds?: string[]; tooltip?: string;
        totalCost?: number; overtimeHours?: number; hoursWorkedThisWeek?: number;
        rateHourly?: number; availability?: any; dragData: any;
    }[];
    sharedCrewIds: Set<string>;
    totalRevenue: number;
    totalLaborCost: number;
    laborPercentage: number | string;
    currency: string;
    startTime: string;
    endTime: string;
    isSpecial: boolean;
    /** True when the group holds OPS task events (rendered as task cards) */
    isOpsTask: boolean;
}

/**
 * Grouped events with per-route metrics (revenue, labor cost, labor %).
 * Special routes (Ops Manager, Reserve, Admin) are marked isSpecial=true
 * and show time range instead of revenue.
 * Crew list is pre-computed per route to avoid template method calls
 * that cause NG0100 during CDK drag operations.
 */
export const selectRouteGroupsWithMetrics = createSelector(
    selectGroupedEventsWithPlaceholders,
    (groups): RouteGroupWithMetrics[] => {
        return groups.map((g) => {
            // A route is "special" if its events are non-job types
            const isSpecial = g.events.some((e) =>
                e.event.type === OPS_MANAGER_EVENT_TYPE
                || e.event.type === RESERVE_EVENT_TYPE
                || e.event.type === NON_BILLABLE_EVENT_TYPE
                || e.event.type === OPS_TASK_EVENT_TYPE
            );
            // OPS tasks render as task cards (not crew-drop rows)
            const isOpsTask = g.events.some((e) => e.event.type === OPS_TASK_EVENT_TYPE);
            const totalRevenue = g.events.reduce(
                (sum, e) => sum + (e.event.discountedSubTotal || 0), 0,
            );
            const totalLaborCost = g.events.reduce(
                (sum, e) => sum + (e.totalLaborCost || 0), 0,
            );
            const laborPercentage = totalRevenue > 0
                ? ((totalLaborCost / totalRevenue) * 100).toFixed(1)
                : 0;
            const currency = g.events[0]?.event?.job?.currency || 'USD';

            const startTimes = g.events
                .map((e) => e.event.start)
                .filter(Boolean)
                .sort((a, b) => a - b);
            const endTimes = g.events
                .map((e) => e.event.end)
                .filter(Boolean)
                .sort((a, b) => b - a);

            // Zone tz (dayjs.tz default set by TimezoneHelperService) so the
            // row header matches the times rendered on the event cards —
            // plain dayjs.unix() formats in the browser's tz and disagrees
            // with the cards whenever the dispatcher isn't in the zone tz.
            const startTime = startTimes[0]
                ? dayjs.tz(startTimes[0] * 1000).format('h:mm A')
                : '';
            const endTime = endTimes[0]
                ? dayjs.tz(endTimes[0] * 1000).format('h:mm A')
                : '';

            // Pre-compute unique crew across all events in this route
            type RouteCrew = {
                name: string; isCrewLead: boolean; isDriver: boolean;
                userId: string;
                hasConflict: boolean; conflictingEventIds: string[];
                tooltip: string; totalCost: number; overtimeHours: number;
                hoursWorkedThisWeek: number; rateHourly: number; availability: any;
                dragData: { attendee: { user: { id: string }; name: string; role: string } };
            };
            const crewMap = new Map<string, RouteCrew>();
            // Track how many events each crew member is on
            const crewEventCount = new Map<string, number>();
            for (const event of g.events) {
                for (const member of event.crew) {
                    if (!member.user?.id) continue;
                    crewEventCount.set(member.user.id, (crewEventCount.get(member.user.id) || 0) + 1);
                    if (!crewMap.has(member.user.id)) {
                        const isCrewLead = member.role === EventAttendeeRoles.crewLead
                            || member.role === EventAttendeeRoles.assistantCrewLead;
                        const isDriver = member.role === EventAttendeeRoles.driver;
                        crewMap.set(member.user.id, {
                            name: member.name,
                            isCrewLead,
                            isDriver,
                            userId: member.user.id,
                            // Conflict + cost metadata for the chip's red
                            // border, hover stats, and conflict tooltip.
                            hasConflict: member.hasConflict || false,
                            conflictingEventIds: member.conflictingEventIds || [],
                            tooltip: member.tooltip || '',
                            totalCost: member.totalCost,
                            overtimeHours: member.overtimeHours,
                            hoursWorkedThisWeek: member.hoursWorkedThisWeek,
                            rateHourly: member.user?.rateHourly,
                            availability: member.user?.availability,
                            dragData: {
                                attendee: {
                                    user: { id: member.user.id },
                                    name: member.name,
                                    role: isCrewLead ? EventAttendeeRoles.crewLead
                                        : isDriver ? EventAttendeeRoles.driver
                                        : EventAttendeeRoles.crewMember,
                                },
                            },
                        });
                    }
                }
            }

            // Split: "shared" crew are on ALL events in the route,
            // "per-event" crew are on only some.
            const totalEvents = g.events.length;
            const sharedCrew: RouteCrew[] = [];
            const sharedCrewIds = new Set<string>();
            for (const [userId, crew] of crewMap) {
                if (crewEventCount.get(userId) === totalEvents) {
                    sharedCrew.push(crew);
                    sharedCrewIds.add(userId);
                }
            }

            // Pre-compute per-event-only crew for each event to avoid
            // calling getEventOnlyCrew in the template (NG0100).
            const eventsWithCrew = g.events.map((event) => {
                const eventOnlyCrew = event.crew
                    .filter((c) => c.user?.id && !sharedCrewIds.has(c.user.id))
                    .map((c) => {
                        const isCrewLead = c.role === EventAttendeeRoles.crewLead
                            || c.role === EventAttendeeRoles.assistantCrewLead;
                        const isDriver = c.role === EventAttendeeRoles.driver;
                        return {
                            name: c.name,
                            isCrewLead,
                            isDriver,
                            userId: c.user.id,
                            role: c.role,
                            // Conflict + cost metadata for the chip's red
                            // border, hover stats, and conflict tooltip.
                            hasConflict: c.hasConflict || false,
                            conflictingEventIds: c.conflictingEventIds || [],
                            tooltip: c.tooltip || '',
                            totalCost: c.totalCost,
                            overtimeHours: c.overtimeHours,
                            hoursWorkedThisWeek: c.hoursWorkedThisWeek,
                            rateHourly: c.user?.rateHourly,
                            availability: c.user?.availability,
                            dragData: {
                                attendee: {
                                    user: { id: c.user.id },
                                    name: c.name,
                                    role: c.role,
                                },
                            },
                        };
                    });
                return { ...event, eventOnlyCrew };
            });

            return {
                ...g,
                groupLabel: g.group.split('__')[0],
                events: eventsWithCrew,
                crew: sharedCrew,
                sharedCrewIds,
                totalRevenue,
                totalLaborCost,
                laborPercentage,
                currency,
                startTime,
                endTime,
                isSpecial,
                isOpsTask,
            };
        });
    }
);